import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, MessageSquare, Image as ImageIcon, Paperclip, AlertCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { useCar } from '@/contexts/CarContext';
import MediaUploader from './MediaUploader';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Message {
  role: 'user' | 'assistant' | 'system' | 'error' | 'fallback';
  content: string;
  images?: string[];
}

interface AIAssistantProps {
  carId?: string;
  initialMessages?: Message[];
  onClose?: () => void;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ 
  carId, 
  initialMessages = [], 
  onClose 
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { cars } = useCar();
  const selectedCar = carId ? cars.find(car => car.id === carId) : null;

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() && uploadedImages.length === 0) return;

    const userMessage: Message = {
      role: 'user',
      content: inputValue,
    };

    // Add user message to chat
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowImageUploader(false);
    setUploadedImages([]);
    setError(null); // Clear any previous errors

    try {
      // Get car details if available
      const carDetails = selectedCar ? {
        brand: selectedCar.brand,
        model: selectedCar.model,
        year: selectedCar.year,
      } : null;

      const response = await supabase.functions.invoke('car-assistant', {
        body: {
          messages: [...messages.filter(m => m.role !== 'error' && m.role !== 'fallback'), userMessage],
          carDetails,
        },
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data.error) {
        // Handle structured error from our edge function
        const errorMessage = response.data.message || 'There was a problem connecting to the AI assistant.';
        setError(errorMessage);
        
        // Add error message to chat
        setMessages((prev) => [...prev, {
          role: 'error',
          content: errorMessage
        }]);
        
        toast({
          title: "AI Assistant Error",
          description: errorMessage,
          variant: "destructive"
        });
        return;
      }

      // Check if this is a fallback response
      if (response.data.fallback) {
        setIsOfflineMode(true);
        const fallbackMessage: Message = {
          role: 'fallback',
          content: response.data.choices[0].message.content,
        };
        
        setMessages((prev) => [...prev, fallbackMessage]);
        
        toast({
          title: "Using Offline Mode",
          description: "The AI service is currently unavailable. Using limited offline responses.",
          variant: "default"
        });
      } else {
        // Normal response
        setIsOfflineMode(false);
        const assistantMessage: Message = {
          role: 'assistant',
          content: response.data.choices[0].message.content,
        };

        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = 'There was a problem connecting to the AI assistant. Please try again later.';
      
      setError(errorMessage);
      
      // Add error message to chat
      setMessages((prev) => [...prev, {
        role: 'error',
        content: errorMessage
      }]);
      
      toast({
        title: "Connection Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleImageUpload = (files: (File | string)[]) => {
    // Convert any strings to Files
    const newFiles = files
      .filter(file => file instanceof File) as File[];
    
    setUploadedImages([...uploadedImages, ...newFiles]);
  };

  return (
    <div className="flex flex-col h-[70vh] rounded-lg border bg-card shadow-sm overflow-hidden pb-16 mb-12">
      {/* Chat header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} />
          <h3 className="font-medium">Car Assistant</h3>
          {isOfflineMode && (
            <div className="flex items-center ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
              <AlertTriangle size={12} className="mr-1" />
              Limited Mode
            </div>
          )}
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <span>×</span>
          </Button>
        )}
      </div>

      {/* Messages container */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <MessageSquare size={24} className="mb-2 opacity-50" />
            <p>Ask me anything about your car!</p>
            <p className="text-sm mt-1">I can help with maintenance tips, troubleshooting, and general advice.</p>
          </div>
        ) : (
          messages.map((message, index) => (
            message.role !== 'system' && (
              <div
                key={index}
                className={cn(
                  "flex",
                  message.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <Card
                  className={cn(
                    "max-w-[80%]",
                    message.role === 'user' 
                      ? "bg-lime text-black" 
                      : message.role === 'error'
                      ? "bg-destructive text-destructive-foreground"
                      : message.role === 'fallback'
                      ? "bg-amber-100 text-amber-900"
                      : "bg-muted"
                  )}
                >
                  <CardContent className="p-3">
                    {message.role === 'error' ? (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                    ) : message.role === 'fallback' ? (
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-1 flex-shrink-0" />
                        <div>
                          <p className="whitespace-pre-wrap">{message.content}</p>
                          <p className="text-xs mt-2 italic">Using limited offline responses due to service unavailability.</p>
                        </div>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )
          ))
        )}
        {isLoading && (
          <div className="flex justify-start">
            <Card className="bg-muted">
              <CardContent className="p-3 flex items-center">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                <p>Thinking...</p>
              </CardContent>
            </Card>
          </div>
        )}
        {error && !isLoading && messages.length === 0 && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {isOfflineMode && !isLoading && messages.length > 0 && (
          <Alert variant="default" className="mt-4 bg-amber-50 text-amber-800 border-amber-200">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              AI service is temporarily unavailable. Using limited offline responses. Some information may not be accurate or personalized.
            </AlertDescription>
          </Alert>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Image uploader */}
      {showImageUploader && (
        <div className="px-3 py-2 border-t">
          <MediaUploader
            maxFiles={3}
            onUpload={handleImageUpload}
            onRemoveExisting={(index) => {
              const newFiles = [...uploadedImages];
              newFiles.splice(index, 1);
              setUploadedImages(newFiles);
            }}
          />
        </div>
      )}

      {/* Input area */}
      <div className="p-3 border-t mb-2">
        <div className="flex items-end gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            type="button"
            onClick={() => setShowImageUploader(!showImageUploader)}
          >
            <Paperclip size={18} />
          </Button>
          <div className="flex-1 relative">
            <Textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your car..."
              className="min-h-[60px] resize-none pr-12"
              disabled={isLoading}
            />
            <Button
              className="absolute bottom-2 right-2 h-8 w-8 p-0"
              onClick={handleSendMessage}
              disabled={isLoading || (!inputValue.trim() && uploadedImages.length === 0)}
            >
              {isLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
              <span className="sr-only">Send</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
